import { editsOf, findSection, isFullConfiguration, value } from '../../parser/fortigate'
import { finding, type Finding, type Rule } from '../types'

/** Community strings that ship as defaults and are the first thing any scanner tries. */
const DEFAULT_COMMUNITIES = ['public', 'private', 'community', 'manager', 'monitor']

export const noRemoteLogging: Rule = {
  id: 'fg-log-no-syslog',
  title: 'No remote logging configured',
  run(config) {
    if (!isFullConfiguration(config)) return []
    const syslog = findSection(config, 'log', 'syslogd', 'setting')
    const enabled = value(syslog, 'status') === 'enable' && value(syslog, 'server') !== undefined
    const fortianalyzer = findSection(config, 'log', 'fortianalyzer', 'setting')
    const fazEnabled = value(fortianalyzer, 'status') === 'enable'
    if (enabled || fazEnabled) return []

    return [
      finding(noRemoteLogging, {
        severity: 'medium',
        title: 'Logs are not shipped anywhere off the device',
        detail:
          'Neither log syslogd setting nor log fortianalyzer setting is enabled with a destination. ' +
          'Local storage is small and circular, and an attacker who reaches the device can clear it — ' +
          'so the record you would need after an incident is the one you are least likely to still have.',
        location: 'log syslogd setting',
        line: syslog?.line,
        pci: '10.3.3',
        remediation:
          'config log syslogd setting\n    set status enable\n    set server "10.0.0.50"\n' +
          '    set port 514\nend',
      }),
    ]
  },
}

export const noNtp: Rule = {
  id: 'fg-no-ntp',
  title: 'Time synchronisation not enabled',
  run(config) {
    if (!isFullConfiguration(config)) return []
    const ntp = findSection(config, 'system', 'ntp')
    if (value(ntp, 'ntpsync') === 'enable') return []

    return [
      finding(noNtp, {
        severity: 'low',
        title: 'The device clock is not synchronised',
        detail:
          'system ntp does not have ntpsync enabled. Log timestamps will drift, which makes correlating ' +
          'this device against any other system unreliable — and correlation is most of what log review is.',
        location: 'system ntp',
        line: ntp?.line,
        pci: '10.6.1',
        remediation: 'config system ntp\n    set ntpsync enable\n    set type default\nend',
      }),
    ]
  },
}

export const defaultSnmpCommunity: Rule = {
  id: 'fg-snmp-default-community',
  title: 'Default SNMP community string',
  run(config) {
    const out: Finding[] = []

    for (const community of editsOf(config, 'system', 'snmp', 'community')) {
      const name = value(community, 'name')
      if (name === undefined || !DEFAULT_COMMUNITIES.includes(name)) continue

      out.push(
        finding(defaultSnmpCommunity, {
          severity: 'high',
          title: `SNMP community "${name}" is a well-known default`,
          detail:
            `An SNMP community named "${name}" is configured. SNMP v1 and v2c send the community string ` +
            'in cleartext and it acts as the only credential, so a default value means the device inventory, ' +
            'interfaces, routes and firmware version are readable by anyone who can reach the SNMP port.',
          location: `system snmp community ${community.key}`,
          line: community.line,
          pci: '2.2.2',
          remediation:
            'config system snmp user\n    edit "monitoring"\n        set security-level auth-priv\n' +
            '        set auth-proto sha256\n        set priv-proto aes256\n    next\nend\n' +
            `# then remove the v1/v2c community:\nconfig system snmp community\n    delete ${community.key}\nend`,
        }),
      )
    }

    return out
  },
}

export const snmpV1V2cEnabled: Rule = {
  id: 'fg-snmp-v1-v2c',
  title: 'SNMP v1/v2c in use',
  run(config) {
    const out: Finding[] = []

    for (const community of editsOf(config, 'system', 'snmp', 'community')) {
      if (value(community, 'status') === 'disable') continue
      const name = value(community, 'name')
      // A default name is already reported, with stronger wording, by its own rule.
      if (name !== undefined && DEFAULT_COMMUNITIES.includes(name)) continue

      out.push(
        finding(snmpV1V2cEnabled, {
          severity: 'medium',
          title: `SNMP community ${community.key} uses v1/v2c`,
          detail:
            'A community-based SNMP entry is configured. v1 and v2c have no encryption and no real ' +
            'authentication — the community string travels in cleartext on every poll.',
          location: `system snmp community ${community.key}`,
          line: community.line,
          pci: '2.2.7',
          remediation:
            'config system snmp user\n    edit "monitoring"\n        set security-level auth-priv\n' +
            '        set auth-proto sha256\n        set priv-proto aes256\n    next\nend',
        }),
      )
    }

    return out
  },
}

export const noPasswordPolicy: Rule = {
  id: 'fg-no-password-policy',
  title: 'No administrator password policy',
  run(config) {
    if (!isFullConfiguration(config)) return []
    const policy = findSection(config, 'system', 'password-policy')
    if (value(policy, 'status') === 'enable') return []

    return [
      finding(noPasswordPolicy, {
        severity: 'medium',
        title: 'No password policy is enforced for administrators',
        detail:
          'system password-policy is absent or disabled, so nothing prevents a short or reused ' +
          'administrator password from being set on a device that controls the network perimeter.',
        location: 'system password-policy',
        line: policy?.line,
        pci: '8.3.6',
        remediation:
          'config system password-policy\n    set status enable\n    set min-length 14\n' +
          '    set min-lower-case-letter 1\n    set min-upper-case-letter 1\n' +
          '    set min-number 1\n    set min-non-alphanumeric 1\nend',
      }),
    ]
  },
}

export const HYGIENE_RULES: Rule[] = [
  noRemoteLogging,
  noNtp,
  defaultSnmpCommunity,
  snmpV1V2cEnabled,
  noPasswordPolicy,
]
